/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2021-12-21 10:21:40
 *
 *******************************************************/
//flow
import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Modal, Stack, ButtonGroup, Button, Badge } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { reduxForm, Field, getFormValues, reset } from 'redux-form';
import { RenderSelect3 } from 'src/components/views/common/forms/render-select-3';
import {
  DropdownIndicator,
  styles
} from 'src/components/views/theme-settings/form/react-select-style';
import styled from 'styled-components';
import * as themeSetupV3Action from 'actions/theme-setup-v3-actions';
import * as ThemeSettingAction from 'src/stores/actions/theme-settings-actions';
import { mixpanelV3Helper } from 'src/helpers/mixpanel-v3';
import { trackFSEvent } from 'src/integrations/fullStory';
import { isThemeV3 } from 'src/helpers/theme-setup-helper';

type State = {};

type Props = {
  destThemeName: string,
  destThemeId: string,
  active: boolean,
  handleCloseModal: Function,
  handleSaveBeforeClone: Function,
  loadingSaveSetting: boolean,
  isPristine: boolean
} & WithI18nProps;
// DONE

// const MAP_BOOST_APP_VER = {
//   v1: '1.0',
//   v2: '2.0',
//   v3: '3.0',
//   mixedV3: '3.0'
// };

const TextNormal = styled.p`
  color: #202223;
`;

class CloneSettingModal extends Component<Props, State> {
  state = {
    step: 1
  };

  componentDidMount() {
    this.props.getThemes();
  }

  componentWillReceiveProps(nextProps) {
    const { active, clonedSourceThemeSettingLoading } = this.props;
    const {
      active: nextActive,
      resetForm,
      clonedSourceThemeSettingLoading: nextClonedSourceThemeSettingLoading,
      clonedSourceThemeSettingSuccess: nextClonedSourceThemeSettingSuccess,
      handleCloseModal
    } = nextProps;
    if (active && !nextActive) {
      this.handlePreviousStep();
      resetForm();
    }

    // Close modal when get cloned source theme setting success
    if (
      clonedSourceThemeSettingLoading &&
      !nextClonedSourceThemeSettingLoading &&
      nextClonedSourceThemeSettingSuccess
    ) {
      handleCloseModal();
    }
  }

  render() {
    const {
      i18n,
      active,
      pristine,
      handleCloseModal,
      themes,
      sourceTheme,
      destThemeName,
      destThemeId,
      loadingSaveSetting,
      clonedSourceThemeSettingLoading,
      isPristine
    } = this.props;
    const { step } = this.state;

    let themesList = themes
      .filter(
        item => item.id !== Number(destThemeId) && isThemeV3(item) //MAP_BOOST_APP_VER[item?.themeLibVersion] === '3.0'
      )
      .map(item => {
        return {
          label: (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <p className="type-theme">
                  {item?.boostThemeLib === 'default'
                    ? i18n.translate('AdminHTML.ThemeSetting.CloneSetting.defaultTheme')
                    : i18n.translate('AdminHTML.ThemeSetting.CloneSetting.matchedTheme')}
                </p>
                <TextNormal>
                  <b style={{ marginRight: '4px' }}>{item?.name}</b>
                  <span>
                    {/* (Boost app ver. {MAP_BOOST_APP_VER[item?.themeLibVersion]} - OS{' '} */}
                    (OS {item?.isOS20 ? '2.0' : '1.0'})
                  </span>
                </TextNormal>
              </div>

              <div>
                {item?.published && (
                  <span
                    style={{
                      marginLeft: '5px',
                      color: '#202223'
                    }}>
                    <Badge status="success">
                      {i18n.translate('AdminHTML.ThemeSetup.ThemeSetup.badgeLive')}
                    </Badge>
                  </span>
                )}
              </div>
            </div>
          ),
          value: item.id,
          name: item?.name
        };
      });

    return (
      <Modal
        open={active}
        onClose={handleCloseModal}
        title={
          step === 1
            ? i18n.translate('AdminHTML.ThemeSetting.CloneSetting.titleClone', {
                theme: destThemeName
              })
            : i18n.translate('AdminHTML.ThemeSetting.CloneSetting.titleConfirmClone', {
                theme: <b>{sourceTheme?.name}</b>
              })
        }
        footer={
          <div className="modal-footer-clone-setting">
            <Stack distribution="trailing" alignment="center">
              <Stack.Item>
                <div className="Polaris-Stack__Item--custom">
                  <ButtonGroup>
                    <Button onClick={handleCloseModal}>
                      {i18n.translate('AdminHTML.Common.Action.cancel')}
                    </Button>
                    <Button
                      onClick={step === 1 ? this.handleNextStep : this.handleClone}
                      primary
                      loading={
                        step === 1
                          ? loadingSaveSetting
                          : clonedSourceThemeSettingLoading && !pristine
                      }
                      disabled={(step === 1 && !sourceTheme) || loadingSaveSetting}>
                      {step === 1
                        ? isPristine
                          ? i18n.translate('AdminHTML.Common.Action.next')
                          : i18n.translate('AdminHTML.ThemeSetting.CloneSetting.saveNext')
                        : i18n.translate('AdminHTML.ThemeSetting.CloneSetting.confirmClone')}
                    </Button>
                  </ButtonGroup>
                </div>
              </Stack.Item>
            </Stack>
          </div>
        }>
        <Modal.Section>
          <div className="modal-content-clone-setting">
            {step === 1 ? (
              <div>
                {!isPristine && (
                  <p style={{ marginBottom: '12px' }}>
                    {i18n.translate('AdminHTML.ThemeSetting.CloneSetting.savedBeforeCloned', {
                      theme: <b>{destThemeName}</b>
                    })}
                  </p>
                )}
                <p style={{ marginBottom: '4px' }}>
                  {i18n.translate('AdminHTML.ThemeSetting.CloneSetting.note', {
                    theme: <b>{destThemeName}</b>
                  })}
                </p>
                <Field
                  name="sourceTheme"
                  component={RenderSelect3}
                  options={themesList}
                  placeholder={i18n.translate(
                    'AdminHTML.ThemeSetting.CloneSetting.chooseATheme_placeholder'
                  )}
                  components={{ DropdownIndicator, SingleValue }}
                  styles={{
                    ...styles,
                    menuList: (provided, state) => ({ ...provided, maxHeight: 300 })
                  }}
                  className="react-select-container"
                  classNamePrefix="react-select-clone-setting"
                  isSearchable={false}
                />
              </div>
            ) : (
              <div>
                <p>{i18n.translate('AdminHTML.ThemeSetting.CloneSetting.sureToClone')}</p>
              </div>
            )}
          </div>
        </Modal.Section>
      </Modal>
    );
  }

  handlePreviousStep = () => {
    this.setState({ step: 1 });
  };

  handleNextStep = () => {
    const { isPristine, handleSaveBeforeClone } = this.props;
    if (!isPristine) {
      handleSaveBeforeClone();
    }
    this.setState({ step: 2 });
  };

  handleClone = () => {
    const { getClonedSourceThemeSetting, sourceTheme } = this.props;
    if (!sourceTheme?.value) {
      return;
    }

    getClonedSourceThemeSetting(sourceTheme?.value);
    // Tracking event
    mixpanelV3Helper.trackEventData('Clone theme visual settings', {
      feature: 'Theme Settings',
      selectedTheme: sourceTheme?.name
    });

    // Track Full Story event
    trackFSEvent('Clone theme visual settings', {
      feature: 'Visual Editor',
      selectedTheme: sourceTheme?.name
    });
  };
}

const SingleValue = ({ data }) => {
  return <p>{data?.name}</p>;
};

const mapStateToProps = (state, props) => {
  const formValues = getFormValues('CloneSettingForm')(state);
  return {
    clonedSourceThemeSettingLoading: _.get(
      state,
      'themeSetting.clonedSourceThemeSettingLoading',
      false
    ),
    clonedSourceThemeSettingSuccess: _.get(
      state,
      'themeSetting.clonedSourceThemeSettingSuccess',
      false
    ),
    themes: _.get(state, 'themeSetupV3.themes', []),
    sourceTheme: _.get(formValues, 'sourceTheme', null)
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  resetForm: () => dispatch(reset('CloneSettingForm')),
  getThemes: () => dispatch(themeSetupV3Action.getThemes(ownProps.i18n)),
  getClonedSourceThemeSetting: themeId =>
    dispatch(ThemeSettingAction.getClonedSourceThemeSettingById(themeId, ownProps.i18n))
});

const CloneSettingForm = reduxForm({
  form: 'CloneSettingForm', // a unique identifier for this form
  enableReinitialize: true
})(CloneSettingModal);

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(CloneSettingForm));
